import React, { useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { KeyValue, KeyValueGeneric } from '@/main/models/common';
import { IntegrationCodeTypeEnum } from '@/main/services/enum/IntegrationCodeTypeEnum';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';

import FiltersMappingTypeSupplier, {
  INITIAL_FORM_STATE_SUPPLIER
} from '@/main/screens/integration/mappingCodes/dinamicFiltersMappingCodes/filtersMappingTypeSupplier';
import FiltersMappingTypeAddress, {
  INITIAL_FORM_STATE_ADDRESS
} from './filtersMappingTypeAddress';
import FiltersMappingTypeAssessmentType, {
  INITIAL_FORM_STATE_ASSESSMENT_TYPE
} from './filtersMappingTypeAssessmentType';
import FiltersMappingTypeBodyshop, {
  INITIAL_FORM_STATE_BODYSHOP
} from './filtersMappingTypeBodyshop';
import FiltersMappingTypeClaimFileCategory, {
  INITIAL_FORM_STATE_CLAIM_FILE_CATEGORY
} from './filtersMappingTypeClaimFileCategory';
import FiltersMappingTypeInsuranceSubline, {
  INITIAL_FORM_STATE_INSURANCE_SUBLINE
} from './filtersMappingTypeInsuranceSubline';
import FiltersMappingTypeInsurer, {
  INITIAL_FORM_STATE_INSURER
} from './filtersMappingTypeInsurer';
import FiltersMappingTypeMake, {
  INITIAL_FORM_STATE_MAKE
} from './filtersMappingTypeMake';
import FiltersMappingTypeModel, {
  INITIAL_FORM_STATE_MODEL
} from './filtersMappingTypeModel';
import FiltersMappingTypePart, {
  INITIAL_FORM_STATE_PART
} from './filtersMappingTypePart';
import FiltersMappingTypeReasonTotalLoss, {
  INITIAL_FORM_STATE_REASON_TOTAL_LOSS
} from './filtersMappingTypeReasonTotalLoss';
import FiltersMappingTypeSubModel, {
  INITIAL_FORM_STATE_SUBMODEL
} from './filtersMappingTypeSubModel';
import FiltersMappingTypeSupplierByBuyer, {
  INITIAL_FORM_STATE_SUPPLIER_BY_BUYER
} from './filtersMappingTypeSupplierByBuyer';
import FiltersVechile, {
  INITIAL_FORM_STATE_VEHICLE
} from './filtersMappingTypeVehicle';
import FiltersMappingTypeSupervisor, {
  INITIAL_FORM_STATE_SUPERVISOR
} from '@/main/screens/integration/mappingCodes/dinamicFiltersMappingCodes/filtersMappingTypeSupervisor';
import FiltersMappingTypeValuator, {
  INITIAL_FORM_STATE_VALUATOR
} from '@/main/screens/integration/mappingCodes/dinamicFiltersMappingCodes/filtersMappingTypeValuator';

type IdsKeyReducer =
  | 'id-mappingCodes-filters'
  | 'id-mappingCodes-modalMapCodeEdit'
  | 'id-mappingCodes-modalMapCode';

export type DinamicFiltersMappingCodesContextType = {
  watcherCountry: KeyValue | null;
  keyReducer: IdsKeyReducer;
  isEdit?: boolean;
  fieldsRequired?: Array<keyof IMERGED_INITIAL_STATE>;
  isRequiredField: (key: keyof IMERGED_INITIAL_STATE) => boolean;
};
export const DinamicFiltersMappingCodesContext = React.createContext(
  {} as DinamicFiltersMappingCodesContextType
);

export const MERGED_INITIAL_STATE = {
  ...INITIAL_FORM_STATE_BODYSHOP,
  ...INITIAL_FORM_STATE_ASSESSMENT_TYPE,
  ...INITIAL_FORM_STATE_VEHICLE,
  ...INITIAL_FORM_STATE_INSURER,
  ...INITIAL_FORM_STATE_MAKE,
  ...INITIAL_FORM_STATE_MODEL,
  ...INITIAL_FORM_STATE_SUBMODEL,
  ...INITIAL_FORM_STATE_PART,
  ...INITIAL_FORM_STATE_SUPPLIER_BY_BUYER,
  ...INITIAL_FORM_STATE_ADDRESS,
  ...INITIAL_FORM_STATE_INSURANCE_SUBLINE,
  ...INITIAL_FORM_STATE_CLAIM_FILE_CATEGORY,
  ...INITIAL_FORM_STATE_REASON_TOTAL_LOSS,
  ...INITIAL_FORM_STATE_SUPPLIER,
  ...INITIAL_FORM_STATE_SUPERVISOR,
  ...INITIAL_FORM_STATE_VALUATOR,
  code: '' as string
};

export type IMERGED_INITIAL_STATE = typeof MERGED_INITIAL_STATE;
interface IRequiredDefaultFields {
  country: KeyValue | null;
  mappingType: KeyValueGeneric<IntegrationCodeTypeEnum, string> | null;
}
interface IDinamicFiltersMappingCodesProps {
  INITIAL_FORM_STATE_DEFAULT: IRequiredDefaultFields;
  isEdit?: boolean;
  keyReducer: IdsKeyReducer;
  fieldsRequired?: Array<keyof IMERGED_INITIAL_STATE>;
}

function DinamicFiltersMappingCodes({
  isEdit = false,
  fieldsRequired = [],
  keyReducer,
  INITIAL_FORM_STATE_DEFAULT
}: IDinamicFiltersMappingCodesProps) {
  const INITIAL_FORM_STATE = {
    ...INITIAL_FORM_STATE_DEFAULT,
    ...MERGED_INITIAL_STATE
  };

  type IFormFiltersMappingCodes = typeof INITIAL_FORM_STATE;
  const { control, watch } = useFormContext<IFormFiltersMappingCodes>();
  const watcherCountry = watch('country');
  const watcherMappingType = useWatch({
    control,
    name: 'mappingType'
  });

  const isRequiredField = (key: keyof IMERGED_INITIAL_STATE) =>
    fieldsRequired.includes(key);

  if (isNullUndefined(watcherMappingType)) return null;

  const RenderComponentObject = {
    [IntegrationCodeTypeEnum.AssessmentType]: (
      <FiltersMappingTypeAssessmentType />
    ),
    [IntegrationCodeTypeEnum.Bodyshop]: <FiltersMappingTypeBodyshop />,
    [IntegrationCodeTypeEnum.Insurer]: <FiltersMappingTypeInsurer />,
    [IntegrationCodeTypeEnum.Make]: <FiltersMappingTypeMake />,
    [IntegrationCodeTypeEnum.Model]: <FiltersMappingTypeModel />,
    [IntegrationCodeTypeEnum.Part]: <FiltersMappingTypePart />,
    [IntegrationCodeTypeEnum.Submodel]: <FiltersMappingTypeSubModel />,
    [IntegrationCodeTypeEnum.Vehicle]: <FiltersVechile />,
    [IntegrationCodeTypeEnum.SupplierByBuyer]: (
      <FiltersMappingTypeSupplierByBuyer />
    ),
    [IntegrationCodeTypeEnum.Address]: <FiltersMappingTypeAddress />,
    [IntegrationCodeTypeEnum.InsuranceSubline]: (
      <FiltersMappingTypeInsuranceSubline />
    ),
    [IntegrationCodeTypeEnum.ClaimFileCategory]: (
      <FiltersMappingTypeClaimFileCategory />
    ),
    [IntegrationCodeTypeEnum.ReasonTotalLoss]: (
      <FiltersMappingTypeReasonTotalLoss />
    ),
    [IntegrationCodeTypeEnum.Supplier]: <FiltersMappingTypeSupplier />,
    [IntegrationCodeTypeEnum.Supervisor]: <FiltersMappingTypeSupervisor />,
    [IntegrationCodeTypeEnum.Valuator]: <FiltersMappingTypeValuator />
  };

  return (
    <DinamicFiltersMappingCodesContext.Provider
      value={{
        keyReducer,
        isEdit,
        watcherCountry,
        fieldsRequired,
        isRequiredField
      }}>
      {RenderComponentObject[watcherMappingType.key] ?? null}
      <KogInputTextControlledNew
        id="id-mappingCode-txt-code"
        name="code"
        label={getTranslateByKey('information.code')}
        required={isRequiredField('code')}
        control={control}
      />
    </DinamicFiltersMappingCodesContext.Provider>
  );
}

export const useDinamicFiltersMappingCodes = () =>
  useContext(DinamicFiltersMappingCodesContext);

export default DinamicFiltersMappingCodes;
